import React, { useState, useEffect, useRef } from "react";
// import "./PayrollManagement.css";

const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="payroll-video-popup-overlay" onClick={onClose}>
      <div className="payroll-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="payroll-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="payroll-video-container">
          <video ref={videoRef} controls autoPlay className="payroll-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="payroll-video-popup-info">
          <h3>Payroll Management Platform</h3>
          <p>See how our payroll platform automates payroll processing and ensures compliance</p>
        </div>
      </div>
    </div>
  );
};

const PayrollManagement = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("payroll-processing");

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-financial-analyst-reviewing-company-data-44759-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  const handleAccessPayroll = () => {
  // Handle payroll management access
  window.open('/payroll-platform', '_blank');
  console.log("Access payroll management");
};

const openPayrollVideoPopup = () => {
  // Open payroll management video popup
  window.open('/payroll-demo', '_blank');
  console.log("Open payroll management video");
};

const handleExplorePayroll = () => {
  // Handle explore payroll integrations
  window.open('/payroll-integrations', '_blank');
  console.log("Explore payroll integrations");
};

  const features = [
    {
      icon: "💰",
      title: "Automated Payroll Processing",
      description: "Streamline payroll calculations, tax withholdings, and direct deposits with complete automation"
    },
    {
      icon: "📊",
      title: "Real-time Payroll Analytics",
      description: "Monitor payroll costs, track expenses, and analyze labor costs with comprehensive dashboards"
    },
    {
      icon: "⚖️",
      title: "Tax Compliance Management",
      description: "Automated tax calculations, filings, and compliance with federal, state, and local regulations"
    },
    {
      icon: "📱",
      title: "Employee Self-Service",
      description: "Secure portal for employees to access pay stubs, tax forms, and update personal information"
    },
    {
      icon: "🔒",
      title: "Bank-level Security",
      description: "Enterprise-grade security with encryption, multi-factor authentication, and audit trails"
    },
    {
      icon: "🔄",
      title: "Seamless Integrations",
      description: "Connect with accounting software, HR systems, and time tracking platforms"
    }
  ];

 const payrollStages = [
  {
    phase: "Setup",
    icon: "⚙️",
    time: "15-30 minutes",
    title: "Company & Employee Setup",
    steps: [
      "Company information configuration",
      "Employee data import & verification",
      "Pay schedule setup",
      "Tax information configuration"
    ]
  },
  {
    phase: "Configuration",
    icon: "🎯",
    time: "1-2 days",
    title: "Payroll Rules & Policies",
    steps: [
      "Payroll calculation rules setup",
      "Benefits & deductions configuration",
      "Tax withholding setup",
      "Compliance rule configuration"
    ]
  },
  {
    phase: "Integration",
    icon: "🔄",
    time: "2-3 days",
    title: "System Integration & Testing",
    steps: [
      "Banking integration setup",
      "Time tracking system connection",
      "Test payroll processing",
      "Integration validation"
    ]
  },
  {
    phase: "Processing",
    icon: "💰",
    time: "Continuous",
    title: "Live Payroll Processing",
    steps: [
      "Automated payroll calculations",
      "Real-time tax compliance",
      "Payment processing & distribution",
      "Reporting & analytics"
    ]
  }
];

const payrollMetrics = [
  { name: "Payroll Processing", progress: 89 },
  { name: "Tax Compliance", progress: 98 },
  { name: "Employee Self-Service", progress: 76 },
  { name: "Integration Status", progress: 95 }
];

const payrollIntegrations = [
  "ADP", "Workday", "QuickBooks", "Xero", 
  "BambooHR", "Gusto", "Paychex", "Zenefits", 
  "TSheets", "Harvest", "Slack", "Microsoft Teams"
];

const payrollTabs = [
  { id: 'processing', label: 'Payroll Processing' },
  { id: 'tax', label: 'Tax Management' },
  { id: 'reporting', label: 'Reporting' },
  { id: 'compliance', label: 'Compliance' }
];

  const tabs = [
    {
      id: "payroll-processing",
      label: "PAYROLL PROCESSING",
      content: {
        icon: "💸",
        title: "Automated Payroll Calculations",
        description: "Eliminate manual calculations and errors with automated payroll processing, tax calculations, and direct deposit management.",
        features: ["AUTOMATED CALCULATIONS", "DIRECT DEPOSIT", "TAX WITHHOLDINGS", "REAL-TIME PROCESSING"],
        secondary: {
          icon: "⚡",
          title: "Instant Payroll Processing",
          description: "Process payroll in minutes with automated calculations, compliance checks, and instant payment distribution."
        },
        buttonText: "EXPLORE PAYROLL PROCESSING"
      }
    },
    {
      id: "tax-compliance",
      label: "TAX COMPLIANCE →",
      content: {
        icon: "📝",
        title: "Automated Tax Management",
        description: "Stay compliant with automated tax calculations, filings, and payments for federal, state, and local requirements.",
        features: ["AUTOMATED FILINGS", "TAX PAYMENTS", "COMPLIANCE MONITORING", "AUDIT SUPPORT"],
        secondary: {
          icon: "🛡️",
          title: "Risk-Free Compliance",
          description: "Eliminate compliance risks with automated updates for tax laws, forms, and filing requirements."
        },
        buttonText: "EXPLORE TAX COMPLIANCE"
      }
    },
    {
      id: "reporting-analytics",
      label: "REPORTING & ANALYTICS",
      content: {
        icon: "📈",
        title: "Comprehensive Payroll Analytics",
        description: "Gain deep insights into payroll costs, labor expenses, and financial trends with advanced reporting.",
        features: ["REAL-TIME DASHBOARDS", "COST ANALYSIS", "TREND REPORTING", "CUSTOM REPORTS"],
        secondary: {
          icon: "💡",
          title: "Business Intelligence",
          description: "Make data-driven decisions with comprehensive payroll analytics and financial insights."
        },
        buttonText: "EXPLORE ANALYTICS"
      }
    },
    {
      id: "employee-self-service",
      label: "EMPLOYEE SELF-SERVICE",
      content: {
        icon: "👥",
        title: "Employee Portal & Access",
        description: "Empower employees with secure self-service access to pay stubs, tax forms, and personal information.",
        features: ["DIGITAL PAY STUBS", "TAX FORM ACCESS", "PROFILE MANAGEMENT", "MOBILE ACCESS"],
        secondary: {
          icon: "📱",
          title: "Mobile-First Experience",
          description: "Employees can access payroll information anytime, anywhere with our mobile-optimized platform."
        },
        buttonText: "EXPLORE SELF-SERVICE"
      }
    }
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="payroll-container">
      {/* Scroll Progress Bar */}
      <div className="payroll-scroll-progress" style={{width: `${scrollProgress}%`}}></div>

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="payroll-background-animation">
        <div className="payroll-floating-shape payroll-shape-1"></div>
        <div className="payroll-floating-shape payroll-shape-2"></div>
        <div className="payroll-floating-shape payroll-shape-3"></div>
        <div className="payroll-floating-shape payroll-shape-4"></div>
        <div className="payroll-floating-shape payroll-shape-5"></div>
        
        {/* Animated Gradient Orbs */}
        <div className="payroll-gradient-orb payroll-orb-1"></div>
        <div className="payroll-gradient-orb payroll-orb-2"></div>
        <div className="payroll-gradient-orb payroll-orb-3"></div>
        
        {/* SVG Background Animation */}
        <svg className="payroll-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="payroll-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(34, 197, 94, 0.08)" />
              <stop offset="50%" stopColor="rgba(16, 185, 129, 0.05)" />
              <stop offset="100%" stopColor="rgba(101, 163, 13, 0.08)" />
            </linearGradient>
            <linearGradient id="payroll-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(16, 185, 129, 0.06)" />
              <stop offset="50%" stopColor="rgba(34, 197, 94, 0.04)" />
              <stop offset="100%" stopColor="rgba(101, 163, 13, 0.06)" />
            </linearGradient>
            <pattern id="payroll-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#payroll-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#payroll-gradient1)"
            className="payroll-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#payroll-gradient2)"
            opacity="0.8"
            className="payroll-svg-path-2"
          />
        </svg>
      </div>

      {/* Enhanced Hero Section */}
      <section className={`payroll-hero-section ${isVisible ? 'payroll-visible' : ''}`}>
        <div className="payroll-hero-content">
          <div className="payroll-hero-text">
            <div className="payroll-hero-badge">
              <span className="payroll-badge-dot"></span>
              Modern Payroll Management
            </div>
            <h1 className="payroll-hero-title">
              Simplify Your <span className="payroll-gradient-highlight">Payroll Process</span>
            </h1>
            <p className="payroll-hero-description">
              Automate payroll processing, ensure tax compliance, and empower your employees 
              with self-service access. Reduce errors and save time with intelligent payroll automation.
            </p>
            <div className="payroll-hero-buttons">
              <button className="payroll-btn-primary" onClick={openVideoPopup}>
                <span>Watch Platform Demo</span>
                <div className="payroll-btn-icon">▶</div>
              </button>
              <button className="payroll-btn-secondary">
                Start Free Trial
              </button>
              <button className="payroll-btn-tertiary">
                View Pricing →
              </button>
            </div>
            
            {/* Enhanced Stats */}
            <div className="payroll-hero-stats">
              <div className="payroll-stat">
                <div className="payroll-stat-number">99.9%</div>
                <div className="payroll-stat-label">Accuracy Rate</div>
              </div>
              <div className="payroll-stat">
                <div className="payroll-stat-number">70%</div>
                <div className="payroll-stat-label">Time Saved</div>
              </div>
              <div className="payroll-stat">
                <div className="payroll-stat-number">50K+</div>
                <div className="payroll-stat-label">Payrolls Processed</div>
              </div>
              <div className="payroll-stat">
                <div className="payroll-stat-number">100%</div>
                <div className="payroll-stat-label">Compliance</div>
              </div>
            </div>
          </div>

          <div className="payroll-hero-visual">
            {/* Floating Cards */}
            <div className="payroll-floating-card payroll-card-1">
              <div className="payroll-card-icon">💰</div>
              <div className="payroll-card-content">
                <div className="payroll-card-title">Payroll Processed</div>
                <div className="payroll-card-text">$2.4M This Month</div>
              </div>
              <div className="payroll-card-progress"></div>
            </div>
            <div className="payroll-floating-card payroll-card-2">
              <div className="payroll-card-icon">⏱️</div>
              <div className="payroll-card-content">
                <div className="payroll-card-title">Processing Time</div>
                <div className="payroll-card-text">12 Minutes Avg.</div>
              </div>
              <div className="payroll-card-progress"></div>
            </div>
            <div className="payroll-floating-card payroll-card-3">
              <div className="payroll-card-icon">👥</div>
              <div className="payroll-card-content">
                <div className="payroll-card-title">Active Employees</div>
                <div className="payroll-card-text">1,247 Managed</div>
              </div>
              <div className="payroll-card-progress"></div>
            </div>
            
            {/* Main Visual with Glass Morphism */}
            <div className="payroll-main-visual">
              <div className="payroll-glass-card">
                <img 
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1911&q=80" 
                  alt="Payroll management dashboard with analytics" 
                  className="payroll-hero-image"
                />
                <div className="payroll-image-overlay"></div>
                <div className="payroll-glass-content">
                  <h4>Payroll Dashboard</h4>
                  <p>Real-time payroll insights and analytics</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="payroll-scroll-indicator">
          <div className="payroll-scroll-line"></div>
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="payroll-features-section">
        <div className="payroll-section-content">
          <div className="payroll-section-header">
            <span className="payroll-section-badge">Core Features</span>
            <h2>Comprehensive Payroll Solution</h2>
            <p>Everything you need to manage payroll efficiently, accurately, and compliantly</p>
          </div>
          
          <div className="payroll-features-grid">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`payroll-feature-card ${activeFeature === index ? 'payroll-active' : ''}`}
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="payroll-feature-icon-wrapper">
                  <div className="payroll-feature-icon">{feature.icon}</div>
                  <div className="payroll-feature-glow"></div>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className="payroll-feature-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

    {/* Payroll Platform Section with Tabs */}
<section className="hr-copilot-journey-section">
  <div className="hr-journey-container">
    {/* Enhanced Animated Background */}
    <div className="hr-journey-bg-orb hr-journey-orb-1"></div>
    <div className="hr-journey-bg-orb hr-journey-orb-2"></div>
    <div className="hr-journey-bg-orb hr-journey-orb-3"></div>
    <div className="hr-journey-grid-overlay"></div>

    {/* Header with stagger animation */}
    <div className="hr-journey-header">
      <div className="hr-journey-badge" style={{animationDelay: '0.1s'}}>
        <span className="hr-journey-badge-icon">💰</span>
        Complete Payroll Solution
        <div className="hr-journey-badge-shimmer"></div>
      </div>
      <h2 className="hr-journey-title" style={{animationDelay: '0.2s'}}>
        Your <span className="hr-journey-gradient-text">Payroll Copilot</span> Platform
      </h2>
      <p className="hr-journey-subtitle" style={{animationDelay: '0.3s'}}>
        From setup to processing - intelligent payroll management at every stage
      </p>
    </div>

    {/* Tab Navigation */}
    <div className="payroll-platform-tabs">
      {payrollTabs.map((tab) => (
        <button
          key={tab.id}
          className={`payroll-tab-btn ${activeTab === tab.id ? 'payroll-tab-active' : ''}`}
          onClick={() => setActiveTab(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>

    {/* Enhanced Timeline with scroll animations */}
    <div className="hr-journey-timeline">
      {payrollStages.map((step, index) => (
        <div 
          key={index} 
          className="hr-journey-card"
          style={{animationDelay: `${0.4 + index * 0.15}s`}}
        >
          {/* Animated Connection Line */}
          {index < payrollStages.length - 1 && (
            <div className="hr-journey-connector">
              <div className="hr-journey-connector-progress"></div>
              <div className="hr-journey-connector-pulse"></div>
            </div>
          )}

          {/* Card Content */}
          <div className="hr-journey-card-inner">
            {/* Enhanced Phase Badge */}
            <div className="hr-journey-phase-badge">
              <span className="hr-journey-phase-number">0{index + 1}</span>
              <span className="hr-journey-phase-divider"></span>
              <span className="hr-journey-phase-name">{step.phase}</span>
              <div className="hr-journey-phase-glow"></div>
            </div>

            {/* Enhanced Icon with pulse */}
            <div className="hr-journey-icon-wrapper">
              <div className="hr-journey-icon-bg"></div>
              <div className="hr-journey-icon-ripple"></div>
              <span className="hr-journey-icon">{step.icon}</span>
            </div>

            {/* Time with animation */}
            <div className="hr-journey-time">
              <span className="hr-journey-time-icon">⏱</span>
              <span className="hr-journey-time-text">{step.time}</span>
            </div>

            {/* Title */}
            <h3 className="hr-journey-card-title">{step.title}</h3>

            {/* Steps with stagger animation */}
            <div className="hr-journey-steps">
              {step.steps.map((item, itemIndex) => (
                <div 
                  key={itemIndex} 
                  className="hr-journey-step-item"
                  style={{animationDelay: `${itemIndex * 0.1}s`}}
                >
                  <span className="hr-journey-check">
                    <span className="hr-journey-check-inner">✓</span>
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Multiple Hover Effects */}
            <div className="hr-journey-card-glow"></div>
            <div className="hr-journey-card-border-glow"></div>
          </div>
        </div>
      ))}
    </div>

    {/* Enhanced Stats Bar with counters */}
    <div className="hr-journey-stats">
      <div className="hr-journey-stat" style={{animationDelay: '0.1s'}}>
        <div className="hr-journey-stat-value">
          $284K
          <div className="hr-journey-stat-sparkle"></div>
        </div>
        <div className="hr-journey-stat-label">Total Payroll</div>
        <div className="hr-journey-stat-bar"></div>
      </div>
      <div className="hr-journey-stat" style={{animationDelay: '0.2s'}}>
        <div className="hr-journey-stat-value">
          14min
          <div className="hr-journey-stat-sparkle"></div>
        </div>
        <div className="hr-journey-stat-label">Processing Time</div>
        <div className="hr-journey-stat-bar"></div>
      </div>
      <div className="hr-journey-stat" style={{animationDelay: '0.3s'}}>
        <div className="hr-journey-stat-value">
          99.9%
          <div className="hr-journey-stat-sparkle"></div>
        </div>
        <div className="hr-journey-stat-label">Accuracy Rate</div>
        <div className="hr-journey-stat-bar"></div>
      </div>
    </div>
  </div>
</section>

{/* Payroll Analytics Section */}
<section className="copilot-insights-section">
  <div className="copilot-section-content copilot-reversed">
    <div className="copilot-visual-content">
      <div className="copilot-analytics-dashboard">
        <div className="copilot-dashboard-header">
          <div className="copilot-header-left">
            <div className="copilot-ai-badge">
              <span className="copilot-ai-pulse"></span>
              Payroll Copilot
            </div>
            <h4>Payroll Intelligence</h4>
          </div>
          <div className="copilot-live-indicator">
            <span className="copilot-live-dot"></span>
            Live Processing
          </div>
        </div>
        
        <div className="copilot-primary-metric">
          <div className="copilot-metric-label">
            <span>Current Payroll Cycle</span>
            <span className="copilot-metric-trend">↑ 8%</span>
          </div>
          <div className="copilot-metric-value">89%</div>
          <div className="copilot-progress-bar">
            <div className="copilot-progress-fill" style={{width: '89%'}}>
              <div className="copilot-progress-glow"></div>
              <div className="copilot-progress-shimmer"></div>
            </div>
          </div>
        </div>

        <div className="copilot-ai-suggestion">
          <div className="copilot-suggestion-icon">💡</div>
          <div className="copilot-suggestion-content">
            <strong>AI Recommendation:</strong> Optimize tax withholdings - potential savings of $2,400 identified
          </div>
        </div>
        
        <div className="copilot-metrics-grid">
          {payrollMetrics.map((item, index) => (
            <div key={index} className="copilot-metric-card" style={{animationDelay: `${index * 0.1}s`}}>
              <div className="copilot-metric-header">
                <span className="copilot-metric-name">{item.name}</span>
                <span className="copilot-metric-percent">{item.progress}%</span>
              </div>
              <div className="copilot-mini-progress">
                <div 
                  className="copilot-mini-fill" 
                  style={{width: `${item.progress}%`}}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    
    <div className="copilot-text-content">
      <span className="copilot-section-badge">
        <span className="copilot-badge-icon">🤖</span>
        AI-Powered Payroll Copilot
      </span>
      <h2>Your Intelligent Payroll Assistant</h2>
      <p className="copilot-section-description">
        Transform your payroll processing with an AI copilot that works alongside your team. 
        Get real-time compliance updates, automated calculations, and smart recommendations that help you 
        process payroll accurately and efficiently automatically.
      </p>
      <div className="copilot-feature-list">
        <div className="copilot-feature-point">
          <div className="copilot-point-icon">
            <span className="copilot-icon-bg">⚡</span>
          </div>
          <div>
            <h4>Automated Processing</h4>
            <p>AI-powered payroll calculations and automated tax filing</p>
          </div>
        </div>
        <div className="copilot-feature-point">
          <div className="copilot-point-icon">
            <span className="copilot-icon-bg">📊</span>
          </div>
          <div>
            <h4>Real-time Analytics</h4>
            <p>Live payroll insights and cost optimization recommendations</p>
          </div>
        </div>
        <div className="copilot-feature-point">
          <div className="copilot-point-icon">
            <span className="copilot-icon-bg">🛡️</span>
          </div>
          <div>
            <h4>Compliance Monitoring</h4>
            <p>Automated tax regulation updates and compliance tracking</p>
          </div>
        </div>
        <div className="copilot-feature-point">
          <div className="copilot-point-icon">
            <span className="copilot-icon-bg">💰</span>
          </div>
          <div>
            <h4>Cost Optimization</h4>
            <p>Intelligent payroll cost analysis and savings recommendations</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

{/* Security & Integration Section */}
<section className="hrcopilot-conversion-zone">
  <div className="hrcopilot-ambient-background">
    <div className="hrcopilot-glow-sphere hrcopilot-glow-primary"></div>
    <div className="hrcopilot-glow-sphere hrcopilot-glow-secondary"></div>
    <div className="hrcopilot-grid-overlay"></div>
  </div>
  <div className="hrcopilot-conversion-container">
    <div className="hrcopilot-conversion-header">
      <span className="hrcopilot-badge-premium">
        <svg className="hrcopilot-badge-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 2L9.5 6.5H14L10.5 9.5L12 14L8 11L4 14L5.5 9.5L2 6.5H6.5L8 2Z" fill="currentColor"/>
        </svg>
        Enterprise-Grade Security
      </span>
      <h2 className="hrcopilot-conversion-title">
        The <span className="hrcopilot-gradient-text">Watchful Protector</span> of Your Payroll Data
      </h2>
      <p className="hrcopilot-conversion-description">
        With our platform, everything under our watch is secure by default. Whether protecting employee data, 
        ensuring tax compliance, or maintaining financial integrity, our security standards come second to none.
      </p>
    </div>

    {/* Security Badges Grid */}
    <div className="hrcopilot-trust-indicators">
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">🔒</div>
        <div className="hrcopilot-trust-content">
          <strong>AICPA SOC 2</strong>
          <span>Type II Certified</span>
        </div>
      </div>
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">🌐</div>
        <div className="hrcopilot-trust-content">
          <strong>GDPR Compliant</strong>
          <span>Global Data Protection</span>
        </div>
      </div>
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">🏥</div>
        <div className="hrcopilot-trust-content">
          <strong>HIPAA Ready</strong>
          <span>Healthcare Compliance</span>
        </div>
      </div>
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">📜</div>
        <div className="hrcopilot-trust-content">
          <strong>ISO 27001</strong>
          <span>Security Certified</span>
        </div>
      </div>
    </div>
  </div>
</section>

{/* Integration Ecosystem */}
<section className="hr-copilot-integration-section">
  <div className="hr-copilot-container">
    <div className="hr-copilot-content-wrapper">
      <div className="hr-copilot-badge-wrapper">
        <span className="hr-copilot-badge">
          <span className="hr-copilot-badge-dot"></span>
          All Systems Connected
        </span>
      </div>
      
      <h2 className="hr-copilot-heading">
        Ready for <span className="hr-copilot-heading-gradient">Deployment</span>
      </h2>
      
      <p className="hr-copilot-description">
        Connect with your existing HR, accounting, banking, and time tracking systems. 
        Our platform integrates seamlessly with popular business applications to create unified payroll experiences.
      </p>

      {/* Integration Grid */}
      <div className="hr-copilot-integrations-grid">
        {payrollIntegrations.map((tool, index) => (
          <div 
            key={index} 
            className="hr-copilot-integration-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="hr-copilot-card-glow"></div>
            <div className="hr-copilot-integration-icon">
              {tool.charAt(0)}
            </div>
            <span className="hr-copilot-integration-name">{tool}</span>
            <div className="hr-copilot-connection-pulse"></div>
          </div>
        ))}
      </div>

      <button className="hr-copilot-cta-button" onClick={handleExplorePayroll}>
        <span>Explore All Integrations</span>
        <span className="hr-copilot-cta-arrow">→</span>
      </button>
    </div>

    <div className="hr-copilot-visual-wrapper">
      <div className="hr-copilot-visual-background"></div>
      
      <div className="hr-copilot-main-card">
        <div className="hr-copilot-card-header">
          <div className="hr-copilot-header-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span className="hr-copilot-header-title">Integration Ecosystem</span>
        </div>
        
        <div className="hr-copilot-image-wrapper">
          <img 
            src="/images/payroll-integration.png" 
            alt="Payroll Integration Dashboard" 
            className="hr-copilot-dashboard-image"
          />
          <div className="hr-copilot-image-gradient"></div>
          
          {/* Floating Integration Badges */}
          <div className="hr-copilot-floating-badges">
            <div className="hr-copilot-float-badge hr-copilot-float-1">
              <span className="hr-copilot-badge-dot-small"></span>
              HR Systems
            </div>
            <div className="hr-copilot-float-badge hr-copilot-float-2">
              <span className="hr-copilot-badge-dot-small"></span>
              Banking
            </div>
            <div className="hr-copilot-float-badge hr-copilot-float-3">
              <span className="hr-copilot-badge-dot-small"></span>
              Time Tracking
            </div>
            <div className="hr-copilot-float-badge hr-copilot-float-4">
              <span className="hr-copilot-badge-dot-small"></span>
              Accounting
            </div>
          </div>

          {/* Connection Lines */}
          <svg className="hr-copilot-connection-lines" viewBox="0 0 400 300">
            <path className="hr-copilot-line hr-copilot-line-1" d="M50,50 Q200,100 350,50" />
            <path className="hr-copilot-line hr-copilot-line-2" d="M50,150 Q200,180 350,150" />
            <path className="hr-copilot-line hr-copilot-line-3" d="M50,250 Q200,220 350,250" />
          </svg>
        </div>

        {/* Stats Bar */}
        <div className="hr-copilot-stats-bar">
          <div className="hr-copilot-stat-item">
            <span className="hr-copilot-stat-value">30+</span>
            <span className="hr-copilot-stat-label">Integrations</span>
          </div>
          <div className="hr-copilot-stat-divider"></div>
          <div className="hr-copilot-stat-item">
            <span className="hr-copilot-stat-value">99.9%</span>
            <span className="hr-copilot-stat-label">Uptime</span>
          </div>
          <div className="hr-copilot-stat-divider"></div>
          <div className="hr-copilot-stat-item">
            <span className="hr-copilot-stat-value">Real-time</span>
            <span className="hr-copilot-stat-label">Sync</span>
          </div>
        </div>
      </div>

      {/* Orbiting Elements */}
      <div className="hr-copilot-orbit-wrapper">
        <div className="hr-copilot-orbit-item hr-copilot-orbit-1">💰</div>
        <div className="hr-copilot-orbit-item hr-copilot-orbit-2">⚡</div>
        <div className="hr-copilot-orbit-item hr-copilot-orbit-3">🔒</div>
      </div>
    </div>
  </div>
</section>

{/* Payroll Management CTA */}
<section className="hrcopilot-conversion-zone">
  <div className="hrcopilot-ambient-background">
    <div className="hrcopilot-glow-sphere hrcopilot-glow-primary"></div>
    <div className="hrcopilot-glow-sphere hrcopilot-glow-secondary"></div>
    <div className="hrcopilot-grid-overlay"></div>
  </div>
  <div className="hrcopilot-conversion-container">
    <div className="hrcopilot-conversion-header">
      <span className="hrcopilot-badge-premium">
        <svg className="hrcopilot-badge-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 2L9.5 6.5H14L10.5 9.5L12 14L8 11L4 14L5.5 9.5L2 6.5H6.5L8 2Z" fill="currentColor"/>
        </svg>
        AI-Powered Payroll Excellence
      </span>
      <h2 className="hrcopilot-conversion-title">
        Experience the Future of <span className="hrcopilot-gradient-text">Payroll Management</span>
      </h2>
      <p className="hrcopilot-conversion-description">
        Let AI handle payroll complexity while you focus on strategic growth. Our Payroll Copilot automates processing, 
        provides intelligent insights, and elevates your entire payroll strategy.
      </p>
    </div>
    
    <div className="hrcopilot-action-group">
      <button className="hrcopilot-cta-primary" onClick={handleAccessPayroll}>
        <span className="hrcopilot-cta-label">Start Your AI Journey</span>
        <svg className="hrcopilot-cta-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
      <button className="hrcopilot-cta-secondary" onClick={openPayrollVideoPopup}>
        <div className="hrcopilot-play-icon">
          <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
            <path d="M1 1L11 7L1 13V1Z" fill="currentColor"/>
          </svg>
        </div>
        <span className="hrcopilot-cta-label">See It In Action</span>
      </button>
    </div>

    <div className="hrcopilot-trust-indicators">
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">✓</div>
        <div className="hrcopilot-trust-content">
          <strong>30-day</strong>
          <span>Free trial</span>
        </div>
      </div>
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">⚡</div>
        <div className="hrcopilot-trust-content">
          <strong>15-minute</strong>
          <span>Setup time</span>
        </div>
      </div>
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">🛡️</div>
        <div className="hrcopilot-trust-content">
          <strong>Enterprise</strong>
          <span>Security standard</span>
        </div>
      </div>
      <div className="hrcopilot-trust-item">
        <div className="hrcopilot-trust-icon">💬</div>
        <div className="hrcopilot-trust-content">
          <strong>24/7</strong>
          <span>Payroll support</span>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Integration Section */}
    <section className="monica-ai-wrapper">
  <div className="monica-container">
    {/* Hero Section */}
    <div className="monica-hero-section">
      <div className="monica-hero-content">
        <div className="monica-hero-text">
          <h1>
            AI that thinks,
            <br />
            acts, and adapts.
            <br />
            <span className="monica-brand-name">Meet Monica.</span>
          </h1>
          <p className="monica-hero-description">
            Our AI grows with you. Start with the level of autonomy that fits your needs today and scale into fully autonomous AI flows when you're ready.
          </p>
        </div>

        <div className="monica-features-grid">
          <div className="monica-feature-card">
            <div className="monica-feature-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" fill="url(#sparkle-gradient)" opacity="0.2"/>
                <path d="M24 8L26 18L36 20L26 22L24 32L22 22L12 20L22 18L24 8Z" fill="url(#sparkle-gradient)"/>
                <defs>
                  <linearGradient id="sparkle-gradient" x1="12" y1="8" x2="36" y2="32">
                    <stop offset="0%" stopColor="#ff0080"/>
                    <stop offset="100%" stopColor="#ffa500"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3 className="monica-feature-title">Contextual Intelligence</h3>
            <p className="monica-feature-description">
              Monica works on your unique dataset—every suggestion is personalized, not cookie-cutter responses.
            </p>
          </div>

          <div className="monica-feature-card">
            <div className="monica-feature-icon monica-icon-automation">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" fill="url(#automation-gradient)" opacity="0.2"/>
                <path d="M24 10C24 10 18 16 18 24C18 32 24 38 24 38M24 10C24 10 30 16 30 24C30 32 24 38 24 38M24 10V38" stroke="url(#automation-gradient)" strokeWidth="3" strokeLinecap="round"/>
                <defs>
                  <linearGradient id="automation-gradient" x1="18" y1="10" x2="30" y2="38">
                    <stop offset="0%" stopColor="#ff6b35"/>
                    <stop offset="100%" stopColor="#ff8c42"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3 className="monica-feature-title">Proactive Automation</h3>
            <p className="monica-feature-description">
              Spots patterns, suggests fixes, and writes scripts—giving you time back to think bigger.
            </p>
          </div>

          <div className="monica-feature-card">
            <div className="monica-feature-icon monica-icon-learning">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect x="12" y="28" width="6" height="10" rx="2" fill="url(#learning-gradient)"/>
                <rect x="21" y="22" width="6" height="16" rx="2" fill="url(#learning-gradient)"/>
                <rect x="30" y="16" width="6" height="22" rx="2" fill="url(#learning-gradient)"/>
                <defs>
                  <linearGradient id="learning-gradient" x1="12" y1="16" x2="36" y2="38">
                    <stop offset="0%" stopColor="#ff0080"/>
                    <stop offset="50%" stopColor="#ff6b9d"/>
                    <stop offset="100%" stopColor="#ff8c42"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3 className="monica-feature-title">Continuous Learning</h3>
            <p className="monica-feature-description">
              Learns from every interaction to deliver increasingly intelligent recommendations and automations.
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Stats Banner Section */}
    <div className="monica-stats-banner">
      <div className="monica-stats-content">
        <div className="monica-stats-icon">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
            <circle cx="40" cy="40" r="35" fill="url(#stats-gradient)" opacity="0.2"/>
            <path d="M40 15C40 15 30 25 30 40C30 55 40 65 40 65M40 15C40 15 50 25 50 40C50 55 40 65 40 65M40 15V65" stroke="url(#stats-gradient)" strokeWidth="4" strokeLinecap="round"/>
            <defs>
              <linearGradient id="stats-gradient" x1="30" y1="15" x2="50" y2="65">
                <stop offset="0%" stopColor="#ff6b35"/>
                <stop offset="100%" stopColor="#ff8c42"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="monica-stats-text">
          <h2 className="monica-stats-headline">
            <span className="monica-stats-number">40%</span> of IT tasks resolve autonomously,
            <br />
            freeing teams for strategic work.
          </h2>
        </div>
        <div className="monica-stats-action">
          <button className="monica-explore-btn">
            EXPLORE MONICA AI
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</section>
    </div>
  );
};

export default PayrollManagement;